import React, { useState } from 'react';
import { View } from 'react-native';
import { Button, Card, Text, useTheme } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

/**
 * Authoritative, non-editable D-057 programme terms.
 *
 * Both VAT regimes are always shown; the partner's own regime (from the API) is
 * highlighted, and can only be corrected by AllDrivers support. Numeric values
 * prefer the API payload (`/workspace/` `current_terms`) and fall back to the
 * approved standardized constants so a disclosure is never invented or blank.
 */
export interface StandardTermsData {
  currency?: string;
  rate_percent?: number;
  partner_vat_percent?: number;
  intended_total_percent?: number;
  monthly_trial_days?: number;
  /** ALL-13: referred annual plans get the same total trial as monthly. */
  annual_trial_days?: number;
  maturity_days?: number;
  settlement_day?: number;
}

const num = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isFinite(value) ? value : fallback;
const pct = (value: number) => new Intl.NumberFormat('pt-PT', { maximumFractionDigits: 2 }).format(value);

export function StandardTerms({ terms, regime }: { terms?: StandardTermsData | null; regime?: string | null }) {
  const { t } = useTranslation('partner');
  const theme = useTheme();
  const total = num(terms?.intended_total_percent, 20);
  const vat = num(terms?.partner_vat_percent, 23);
  const normalRate = regime === 'normal' && typeof terms?.rate_percent === 'number'
    ? terms.rate_percent : 16.26;
  const monthlyDays = num(terms?.monthly_trial_days, 14);
  const annualDays = num(terms?.annual_trial_days, monthlyDays);
  // ALL-13: no retention period; kept as an interpolation value only.
  const maturityDays = num(terms?.maturity_days, 0);
  const settlementDay = num(terms?.settlement_day, 1);
  const own = (candidate: string) => regime === candidate;
  // Detailed terms are on demand; the brief conditions live in the referral code card.
  const [open, setOpen] = useState(false);

  const block = { gap: 4 } as const;
  return <Card testID="standard-terms" style={{ borderRadius: 16 }}><Card.Content style={{ gap: 12, paddingVertical: 12 }}>
    <Button testID="terms-toggle" icon={open ? 'chevron-up' : 'chevron-down'} contentStyle={{ minHeight: 44 }}
      accessibilityState={{ expanded: open }} onPress={() => setOpen(value => !value)}>{t('termsTitle')}</Button>
    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('termsFixed')}</Text>

    {open && <>
    <View style={block}>
      <Text variant="labelLarge">{t('termsCustomer')}</Text>
      <Text variant="bodyMedium">{t('termsMonthly', { days: monthlyDays })}</Text>
      <Text variant="bodyMedium">{t('termsAnnual', { days: annualDays })}</Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('termsNoCoupons')}</Text>
    </View>

    <View style={block}>
      <Text variant="labelLarge">{t('termsEarn')}</Text>
      <View testID="regime-exempt" style={{ gap: 2 }}>
        <Text variant="bodyMedium">{t('termsExempt', { total: pct(total) })}</Text>
        {own('exempt') && <Text accessibilityLabel={t('yourRegime')} variant="labelMedium" style={{ color: theme.colors.primary }}>{t('yourRegime')}</Text>}
      </View>
      <View testID="regime-normal" style={{ gap: 2 }}>
        <Text variant="bodyMedium">{t('termsNormal', { rate: pct(normalRate), vat: pct(vat), total: pct(total) })}</Text>
        {own('normal') && <Text accessibilityLabel={t('yourRegime')} variant="labelMedium" style={{ color: theme.colors.primary }}>{t('yourRegime')}</Text>}
      </View>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('termsRecurring')}</Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
        {own('normal') ? t('termsVatNormal') : own('exempt') ? t('termsVatExempt') : t('termsVatUnknown')}
      </Text>
    </View>

    <View style={block}>
      <Text variant="labelLarge">{t('termsExcludedTitle')}</Text>
      <Text variant="bodyMedium">{t('termsExcluded')}</Text>
      <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('termsMaturity', { days: maturityDays, day: settlementDay })}</Text>
    </View>
    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>{t('termsOneBenefit')}</Text>
    </>}
  </Card.Content></Card>;
}
